import React from "react";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { UserAuthProvider, useUserAuth } from "@/context/UserAuthContext";
import Login from "@/pages/Login";
import AdminLogin from "@/pages/AdminLogin";
import Register from "@/pages/Register";
import ResetPassword from "@/pages/ResetPassword";
import AuthCallback from "@/pages/AuthCallback";
import UserPanel from "@/pages/UserPanel";
import { AppLock } from "@/components/AppLock";
import Community from "@/pages/Community";
import Feed from "@/pages/Feed";
import Market from "@/pages/Market";
import Dashboard from "@/pages/Dashboard";
import Users from "@/pages/Users";
import Jobs from "@/pages/Jobs";
import Costs from "@/pages/Costs";
import MarketAdmin from "@/pages/MarketAdmin";
import Matrix from "@/pages/Matrix";
import Cloner from "@/pages/Cloner";
import ClonerEditor from "@/pages/ClonerEditor";
import Audit from "@/pages/Audit";
import Errors from "@/pages/Errors";
import Admins from "@/pages/Admins";
import Editor from "@/pages/Editor";
import Upload from "@/pages/Upload";
import Requests from "@/pages/Requests";
import Conversations from "@/pages/Conversations";
import Profile from "@/pages/Profile";
import Friends from "@/pages/Friends";
import ImageEntry from "@/pages/ImageEntry";
import PaymentResult from "@/pages/PaymentResult";
import Pricing from "@/pages/Pricing";
import { ForgeGateHost } from "@/components/ForgeGate";

const Checking = () => (
  <div className="min-h-screen flex items-center justify-center" style={{ backgroundColor: "#05070C" }}
    data-testid="auth-checking">
    <div className="w-10 h-10 rounded-full border-2 border-red-500/30 border-t-red-500 animate-spin" />
  </div>
);

const Protected = ({ children }) => {
  const { isAuthed, checking } = useAuth();
  if (checking) return <Checking />;
  return isAuthed ? children : <Navigate to="/acceso" replace />;
};

const UserProtected = ({ children }) => {
  const { isAuthed, checking } = useUserAuth();
  if (checking) return <Checking />;
  return isAuthed ? <AppLock>{children}</AppLock> : <Navigate to="/acceso" replace />;
};

const Router = () => {
  const location = useLocation();
  if (location.hash?.includes("session_id=")) return <AuthCallback />;
  return (
    <Routes>
      <Route path="/acceso" element={<Login />} />
      <Route path="/login" element={<Navigate to="/acceso" replace />} />
      <Route path="/admin" element={<AdminLogin />} />
      <Route path="/acceso-admin" element={<Navigate to="/admin" replace />} />
      <Route path="/registro" element={<Register />} />
      <Route path="/restablecer" element={<ResetPassword />} />
      <Route path="/panel" element={<UserProtected><UserPanel /></UserProtected>} />
      <Route path="/comunidad" element={<UserProtected><Community /></UserProtected>} />
      <Route path="/" element={<UserProtected><Feed /></UserProtected>} />
      <Route path="/muro" element={<UserProtected><Feed /></UserProtected>} />
      <Route path="/mercado" element={<UserProtected><Market /></UserProtected>} />
      <Route path="/perfil/:key" element={<UserProtected><Profile /></UserProtected>} />
      <Route path="/amigos" element={<UserProtected><Friends /></UserProtected>} />
      <Route path="/editor/:jobId" element={<Editor />} />
      <Route path="/imagen" element={<ImageEntry />} />
      <Route path="/precios" element={<Pricing />} />
      <Route path="/matrix" element={<Matrix />} />
      <Route path="/clonador" element={<UserProtected><Cloner /></UserProtected>} />
      <Route path="/clonador/:siteId" element={<UserProtected><ClonerEditor /></UserProtected>} />
      <Route path="/pago/exito" element={<UserProtected><PaymentResult /></UserProtected>} />
      <Route path="/pago/cancelado" element={<UserProtected><PaymentResult cancelled /></UserProtected>} />
      <Route path="/dashboard" element={<Protected><Dashboard /></Protected>} />
      <Route path="/solicitudes" element={<Protected><Requests /></Protected>} />
      <Route path="/conversaciones" element={<Protected><Conversations /></Protected>} />
      <Route path="/subir" element={<Protected><Upload /></Protected>} />
      <Route path="/users" element={<Protected><Users /></Protected>} />
      <Route path="/jobs" element={<Protected><Jobs /></Protected>} />
      <Route path="/costs" element={<Protected><Costs /></Protected>} />
      <Route path="/admin/mercado" element={<Protected><MarketAdmin /></Protected>} />
      <Route path="/audit" element={<Protected><Audit /></Protected>} />
      <Route path="/errors" element={<Protected><Errors /></Protected>} />
      <Route path="/admins" element={<Protected><Admins /></Protected>} />
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
};

function App() {
  return (
    <AuthProvider>
      <UserAuthProvider>
        <BrowserRouter>
          <Router />
          <ForgeGateHost />
        </BrowserRouter>
      </UserAuthProvider>
    </AuthProvider>
  );
}

export default App;
